import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ReportSharePanel } from '@/components/assessment-engine/ReportSharePanel';
import { AssessmentResultsView } from '@/components/assessment-engine/AssessmentResultsView';
import { AePageHeader, AePageShell } from '@/components/assessment-engine/layout';
import {
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_CARD,
  AE_INPUT,
  AE_LABEL,
  AE_LINK,
  AE_MUTED,
  AE_SECTION_TITLE,
  AE_TEXTAREA,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeCampaignReport, AeNarrativeState } from '@/types/assessmentEngine';

/** Admin aggregate report — same results presentation as respondents, plus export/share/narrative. */
export const AssessmentReportPage: React.FC = () => {
  const { t } = useLanguage();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [report, setReport] = useState<AeCampaignReport | null>(null);
  const [narrative, setNarrative] = useState<AeNarrativeState | null>(null);
  const [publishText, setPublishText] = useState('');
  const [filterFunction, setFilterFunction] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterEmailDomain, setFilterEmailDomain] = useState('');
  const [useAiRefresh, setUseAiRefresh] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadReport = (filters?: Record<string, string>) => {
    if (!campaignId) return;
    assessmentEngineService
      .getCampaignReport(campaignId, filters)
      .then(setReport)
      .catch(() => toast.error(t('assessmentEngine.report.failedLoad')));
  };

  useEffect(() => {
    if (!campaignId) return;
    loadReport();
    assessmentEngineService
      .getNarrative(campaignId)
      .then((n) => {
        setNarrative(n);
        setPublishText(n.published_text ?? n.ai_draft_text ?? '');
      })
      .catch(() => {});
  }, [campaignId, t]);

  if (!report || !campaignId) {
    return (
      <AePageShell>
        <p className={AE_MUTED}>{t('assessmentEngine.report.loading')}</p>
      </AePageShell>
    );
  }

  const displayNarrative = report.narrative ?? narrative;
  const benchmarkB1 =
    report.benchmark_b1 ??
    (report.benchmark?.b1
      ? {
          cohort_met: report.benchmark.b1.cohort_met,
          cohort_count: report.benchmark.b1.cohort_count,
          threshold: report.benchmark.b1.threshold,
          time_period_key: '',
          sections: report.benchmark.b1.sections,
        }
      : null);

  const refreshNarrative = async () => {
    try {
      const n = await assessmentEngineService.refreshNarrative(campaignId!, useAiRefresh);
      setNarrative(n);
      setPublishText(n.published_text ?? n.ai_draft_text ?? '');
      toast.success(t('assessmentEngine.report.refreshDraft'));
    } catch {
      toast.error(t('assessmentEngine.report.failedLoad'));
    }
  };

  const publishNarrative = async () => {
    try {
      const n = await assessmentEngineService.publishNarrative(campaignId, publishText);
      setNarrative(n);
      toast.success(t('assessmentEngine.report.publishNarrative'));
    } catch {
      toast.error(t('assessmentEngine.report.failedLoad'));
    }
  };

  const applyFilters = () => {
    const filters: Record<string, string> = {};
    if (filterFunction.trim()) filters.function = filterFunction.trim();
    if (filterLocation.trim()) filters.location = filterLocation.trim();
    if (filterEmailDomain.trim()) filters.email_domain = filterEmailDomain.trim();
    loadReport(Object.keys(filters).length ? filters : undefined);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      // Export the report as currently viewed: active filters + commentary in the editor.
      const filters: Record<string, string> = {};
      if (filterFunction.trim()) filters.function = filterFunction.trim();
      if (filterLocation.trim()) filters.location = filterLocation.trim();
      if (filterEmailDomain.trim()) filters.email_domain = filterEmailDomain.trim();

      const blob = await assessmentEngineService.exportReportPdf(campaignId, {
        filters: Object.keys(filters).length ? filters : undefined,
        narrative_text: publishText.trim() || null,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessment-report-${campaignId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('assessmentEngine.report.pdfExported'));
    } catch {
      toast.error(t('assessmentEngine.report.pdfFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <AePageShell>
      <AePageHeader
        title={t('assessmentEngine.report.title')}
        breadcrumbs={[
          { label: t('assessmentEngine.breadcrumbs.engine'), to: '/assessment-engine/campaigns' },
          {
            label: t('assessmentEngine.report.title'),
            to: `/assessment-engine/campaigns/${campaignId}?tab=report`,
          },
          { label: t('assessmentEngine.report.fullReport') },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={exporting}
              onClick={exportPdf}
              className={`${AE_BTN_SECONDARY} disabled:opacity-50`}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exporting
                ? t('assessmentEngine.report.exportingPdf')
                : t('assessmentEngine.report.exportPdf')}
            </button>
            <Link to={`/assessment-engine/campaigns/${campaignId}?tab=report`} className={AE_LINK}>
              {t('assessmentEngine.report.backToCampaign')}
            </Link>
          </div>
        }
      />

      <div className="space-y-6">
        {displayNarrative?.narrative_stale && (
          <div className="bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 text-[var(--color-warning)] text-sm rounded p-3">
            {t('assessmentEngine.report.narrativeStale')}
          </div>
        )}

        <div className={`${AE_CARD} space-y-3`}>
          <h2 className={AE_SECTION_TITLE}>{t('assessmentEngine.report.demographicFilters')}</h2>
          <p className={`${AE_MUTED} text-xs`}>
            {t('assessmentEngine.report.filtersFutureHint')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.report.filterFunction')}</label>
              <input
                className={AE_INPUT}
                value={filterFunction}
                onChange={(e) => setFilterFunction(e.target.value)}
              />
            </div>
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.report.filterLocation')}</label>
              <input
                className={AE_INPUT}
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
              />
            </div>
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.report.filterEmailDomain')}</label>
              <input
                className={AE_INPUT}
                value={filterEmailDomain}
                onChange={(e) => setFilterEmailDomain(e.target.value)}
                placeholder="acme.com"
              />
            </div>
          </div>
          <button type="button" onClick={applyFilters} className={AE_BTN_PRIMARY}>
            {t('assessmentEngine.report.applyFilters')}
          </button>
          {report.filters_applied && Object.keys(report.filters_applied).length > 0 && (
            <p className="text-xs text-[var(--color-secondary-text)]">
              {t('assessmentEngine.report.filtersActive')}: {JSON.stringify(report.filters_applied)}
            </p>
          )}
        </div>

        <div className={`${AE_CARD} space-y-3`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className={AE_SECTION_TITLE}>{t('assessmentEngine.report.aiNarrative')}</h2>
              <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                {displayNarrative?.assistive_label ?? t('assessmentEngine.report.assistiveLabel')}
                {displayNarrative?.draft_source && (
                  <span className="ml-2 text-[var(--color-secondary-text)]">
                    ({displayNarrative.draft_source === 'ai' ? 'AI' : 'Rule-based'} draft)
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs text-[var(--color-secondary-text)]">
                <input
                  type="checkbox"
                  checked={useAiRefresh}
                  onChange={(e) => setUseAiRefresh(e.target.checked)}
                />
                {t('assessmentEngine.report.useAiRefresh')}
              </label>
              <button type="button" onClick={refreshNarrative} className={AE_LINK}>
                {t('assessmentEngine.report.refreshDraft')}
              </button>
            </div>
          </div>
          <textarea
            className={AE_TEXTAREA}
            value={publishText}
            onChange={(e) => setPublishText(e.target.value)}
            rows={5}
          />
          <button type="button" onClick={publishNarrative} className={AE_BTN_PRIMARY}>
            {t('assessmentEngine.report.publishNarrative')}
          </button>
        </div>

        <div className={AE_CARD}>
          <p className={`${AE_MUTED} mb-4`}>
            {report.complete_count} complete · {report.response_count} total responses · As of{' '}
            {new Date(report.as_of).toLocaleString()}
          </p>
          <AssessmentResultsView
            results={{
              scale_max: report.scale_max ?? 5,
              sections: report.sections,
              questions: report.questions ?? [],
              scoring_footnote: report.scoring_footnote,
              benchmark_b1: benchmarkB1,
              maturity: report.maturity ?? null,
            }}
            scoreLabel={t('assessmentEngine.results.campaignAverage')}
            overallCapabilityHeadline
          />
        </div>

        <ReportSharePanel campaignId={campaignId} />
      </div>
    </AePageShell>
  );
};
